import {
  useMutation,
  useSuspenseQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
} from "@tanstack/react-router";
import { Suspense } from "react";
import { toast } from "sonner";

import { useTRPC } from "@/infrastructure/trpc/react";
import { SentryConnectionForm } from "@/sentry/components/sentry-connection-form";
import { AppLayout } from "@/shared/layout";
import { Skeleton } from "@/shared/ui/skeleton";
export const Route = createFileRoute(
  "/app/organization/$id/sentry/$connectionId/edit"
)({
  loader: async ({ context, params }) => {
    await context.queryClient
      .query(
        context.trpc.sentry.getByIdWithCredentials.queryOptions(
          {
            organizationId: params.id,
            connectionId: params.connectionId,
          }
        )
      )
      .catch(() => undefined);
  },
  component: RouteComponent,
});

function EditForm({
  organizationId,
  connectionId,
}: {
  organizationId: string;
  connectionId: string;
}) {
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { data } = useSuspenseQuery(
    trpc.sentry.getByIdWithCredentials.queryOptions({
      organizationId,
      connectionId,
    })
  );

  const updateConnection = useMutation(
    trpc.sentry.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Sentry connection updated");
        await queryClient.invalidateQueries(
          trpc.sentry.list.queryOptions({
            organizationId,
          })
        );
        void navigate({
          to: "/app/organization/$id/settings/sentry",
          params: { id: organizationId },
        });
      },
      onError: (error) => {
        toast.error(
          error.message ?? "Failed to update connection"
        );
      },
    })
  );

  const connection = data?.connection;

  if (!connection) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">
          Connection not found
        </p>
      </div>
    );
  }

  return (
    <SentryConnectionForm
      organizationId={organizationId}
      mode="edit"
      initialValues={{
        name: connection.name,
        authToken: connection.authToken,
        sentryOrganizationUrl:
          connection.sentryOrganizationUrl,
      }}
      onSubmit={async (values) => {
        await updateConnection.mutateAsync({
          organizationId,
          connectionId,
          name: values.name,
          authToken: values.authToken,
        });
      }}
      isSubmitting={updateConnection.isPending}
      cardDescription="Update your Sentry connection settings"
      submitButtonText="Save Changes"
      submittingButtonText="Saving..."
    />
  );
}

function RouteComponent() {
  const { id: organizationId, connectionId } =
    Route.useParams();

  return (
    <AppLayout
      title="Edit Sentry Connection"
      headerActions={null}
    >
      <div className="mx-auto max-w-2xl">
        <Suspense
          fallback={
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-64 w-full" />
            </div>
          }
        >
          <EditForm
            organizationId={organizationId}
            connectionId={connectionId}
          />
        </Suspense>
      </div>
    </AppLayout>
  );
}
